import React from "react";
import {
  CCol,
  CFormGroup,
  CInput,
  CInputRadio,
  CLabel,
  CRow,
  CSelect,
  CTextarea,
} from "@coreui/react";
import { getFieldErrors } from "../../../models/errors";
import { FieldErrors } from "../../form/FieldErrors";
import {
  INVOICE_SALE_TYPE_CASH,
  INVOICE_SALE_TYPE_CASH_LABEL,
  INVOICE_SALE_TYPE_CREDIT,
  INVOICE_SALE_TYPE_CREDIT_LABEL,
} from "../../../models/invoice";
import { TRANSACTION_TYPE_CHOICES } from "../../../models/transaction-type";
import { CURRENCY_CHOICES, PYG } from "../../../currency/available-currencies";
import PYGField from "../../currencies/PYGField";
import { useInvoiceFormContext } from "../context/InvoiceFormContext";

const SectionDivider: React.FC<{ icon: string; label: string }> = ({
  icon,
  label,
}) => (
  <div
    style={{
      display: "flex",
      alignItems: "center",
      marginBottom: "16px",
      marginTop: "16px",
    }}
  >
    <div
      style={{
        width: "4px",
        height: "18px",
        background: "#006657",
        borderRadius: "2px",
        marginRight: "10px",
        flexShrink: 0,
      }}
    />
    <i
      className={`fa ${icon} text-primary mr-2`}
      style={{ fontSize: "0.85rem" }}
    ></i>
    <h6
      style={{
        margin: 0,
        fontWeight: 600,
        color: "var(--theme-muted)",
        fontSize: "0.78rem",
        textTransform: "uppercase",
        letterSpacing: "0.8px",
      }}
    >
      {label}
    </h6>
    <div
      style={{
        flex: 1,
        height: "1px",
        background: "var(--theme-divider)",
        marginLeft: "10px",
      }}
    />
  </div>
);

const FormLabel: React.FC<{
  required?: boolean;
  children: React.ReactNode;
}> = ({ required, children }) => (
  <CLabel
    className="mb-0"
    style={{ fontWeight: 600, fontSize: "0.875rem", color: "var(--theme-text-soft)" }}
  >
    {required && <span className="text-danger mr-1">*</span>}
    {children}
  </CLabel>
);

export const InvoiceFormSaleSection: React.FC = () => {
  const {
    invoiceRef,
    saleType,
    currency,
    pygExchangeRate,
    onSaleTypeChange,
    onTransactionTypeCodeChange,
    onCurrencyChange,
    onPygExchangeRateChange,
    onTermChange,
    onObsChange,
    errors,
  } = useInvoiceFormContext();

  const invoice = invoiceRef.current;
  return (
    <>
      <SectionDivider icon="fa-exchange" label="Condiciones de Venta" />
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Condición de venta:</FormLabel>
          </CCol>
          <CCol md="auto">
            <div
              style={{
                display: "inline-flex",
                border: "1px solid var(--theme-border)",
                borderRadius: "6px",
                overflow: "hidden",
              }}
            >
              <label
                htmlFor="saleTypeCash"
                style={{
                  margin: 0,
                  padding: "6px 16px",
                  cursor: "pointer",
                  background:
                    saleType === INVOICE_SALE_TYPE_CASH ? "#006657" : "var(--theme-surface)",
                  color:
                    saleType === INVOICE_SALE_TYPE_CASH ? "white" : "var(--theme-text-soft)",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  transition: "all 0.15s",
                  borderRight: "1px solid var(--theme-border)",
                }}
              >
                <CInputRadio
                  id="saleTypeCash"
                  name="saleType"
                  value={INVOICE_SALE_TYPE_CASH}
                  defaultChecked={invoice?.saleType === INVOICE_SALE_TYPE_CASH}
                  onChange={() => onSaleTypeChange(INVOICE_SALE_TYPE_CASH)}
                  style={{ display: "none" }}
                ></CInputRadio>
                {INVOICE_SALE_TYPE_CASH_LABEL}
              </label>
              <label
                htmlFor="saleTypeCredit"
                style={{
                  margin: 0,
                  padding: "6px 16px",
                  cursor: "pointer",
                  background:
                    saleType === INVOICE_SALE_TYPE_CREDIT ? "#006657" : "var(--theme-surface)",
                  color:
                    saleType === INVOICE_SALE_TYPE_CREDIT ? "white" : "var(--theme-text-soft)",
                  fontWeight: 500,
                  fontSize: "0.875rem",
                  transition: "all 0.15s",
                }}
              >
                <CInputRadio
                  id="saleTypeCredit"
                  name="saleType"
                  value={INVOICE_SALE_TYPE_CREDIT}
                  defaultChecked={
                    invoice?.saleType === INVOICE_SALE_TYPE_CREDIT
                  }
                  onChange={() => onSaleTypeChange(INVOICE_SALE_TYPE_CREDIT)}
                  style={{ display: "none" }}
                ></CInputRadio>
                {INVOICE_SALE_TYPE_CREDIT_LABEL}
              </label>
            </div>
            <FieldErrors
              errors={getFieldErrors("saleType", errors) as string[]}
            ></FieldErrors>
          </CCol>
          {saleType === INVOICE_SALE_TYPE_CREDIT && (
            <>
              <CCol md={2}>
                <FormLabel>Plazo:</FormLabel>
              </CCol>
              <CCol>
                <CInput
                  type="text"
                  defaultValue={invoice?.term || ""}
                  onChange={onTermChange}
                  placeholder="Plazo"
                ></CInput>
                <FieldErrors
                  errors={getFieldErrors("term", errors) as string[]}
                ></FieldErrors>
              </CCol>
            </>
          )}
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel required>Tipo de Transacción:</FormLabel>
          </CCol>
          <CCol md={4}>
            <CSelect
              type="text"
              defaultValue={invoice?.transactionTypeCode}
              onChange={onTransactionTypeCodeChange}
            >
              <option value={""}>-----</option>
              {Array.from(TRANSACTION_TYPE_CHOICES.entries()).map((entry) => {
                return (
                  <option key={entry[0]} value={entry[0]}>
                    {entry[1]}
                  </option>
                );
              })}
            </CSelect>
            <FieldErrors
              errors={getFieldErrors("transactionTypeCode", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel>Moneda:</FormLabel>
          </CCol>
          <CCol md={2}>
            <CSelect
              type="text"
              defaultValue={invoice?.currency || PYG}
              onChange={onCurrencyChange}
            >
              <option value={""} disabled>
                -----
              </option>
              {Array.from(CURRENCY_CHOICES.entries()).map((entry) => {
                return (
                  <option key={entry[0]} value={entry[0]}>
                    {entry[1]}
                  </option>
                );
              })}
            </CSelect>
            <FieldErrors
              errors={getFieldErrors("currency", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
      {currency !== PYG && (
        <CFormGroup>
          <CRow className="align-items-center">
            <CCol md={2}>
              <FormLabel required>Cambio:</FormLabel>
            </CCol>
            <CCol md={2}>
              <PYGField
                placeholder="Total"
                limit={1000000000000}
                onChange={onPygExchangeRateChange}
                value={pygExchangeRate}
                decimalsLimit={2}
              />
              <FieldErrors
                errors={getFieldErrors("pygExchangeRate", errors) as string[]}
              ></FieldErrors>
            </CCol>
          </CRow>
        </CFormGroup>
      )}
      <CFormGroup>
        <CRow className="align-items-center">
          <CCol md={2}>
            <FormLabel>Observación:</FormLabel>
          </CCol>
          <CCol md={10}>
            <CTextarea
              placeholder="Información adicional"
              defaultValue={invoice?.obs}
              onChange={onObsChange}
              style={{ resize: "vertical", minHeight: "70px" }}
            ></CTextarea>
            <FieldErrors
              errors={getFieldErrors("obs", errors) as string[]}
            ></FieldErrors>
          </CCol>
        </CRow>
      </CFormGroup>
    </>
  );
};
